import { StyledBadge } from "@/components/ui/styled-badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EventActionsDropdown } from "@/components/event-actions-dropdown";
import { useAuth } from "@/hooks/use-auth";
import type { EventWithOrganisationAndPerformerCount } from "@shared/schema";
import { format } from "date-fns";
import { Calendar, Link, Lock, MapPin, Users } from "lucide-react";
import { useState } from "react";

interface EventCardProps {
  event: EventWithOrganisationAndPerformerCount;
  onSelect: (jamNightId: number) => void;
}

export function EventCard({ event, onSelect }: EventCardProps) {
  const { user } = useAuth();
  const [preventNavigation, setPreventNavigation] = useState(false);

  const canToggleCompletion = user?.role === 'god' || user?.organisationUsers?.some(
    ou => ou.organisation.id === event.organisationId && ou.role === 'owner'
  );

  const handleSelect = () => {
    if (!preventNavigation) {
      onSelect(event.id);
    }
  };

  return (
    <Card
      key={event.id}
      className={`cursor-pointer group relative overflow-hidden border-0 backdrop-blur-xl shadow-2xl hover:shadow-3xl transition-all duration-500 ease-out hover:scale-[1.02] flex flex-col h-full ${
        event.completed ? 'bg-gray-200/30 hover:bg-gray-200/40' : 'bg-white/20 hover:bg-white/30'
      }`}
      style={{
        background: event.completed
          ? 'linear-gradient(135deg, rgba(156,163,175,0.3) 0%, rgba(156,163,175,0.15) 100%)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
        borderRadius: '20px',
        border: event.completed
          ? '1px solid rgba(156,163,175,0.3)'
          : '1px solid rgba(255,255,255,0.2)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
      }}
      onClick={handleSelect}
    >
      {/* Liquid Glass Specular Highlight */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
          pointerEvents: 'none',
        }}
      />
      
      {/* Dynamic Glass Refraction Effect */}
      <div 
        className="absolute top-0 left-0 w-full h-[1px] opacity-60"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)',
        }}
      />
      
      {/* Event Actions Dropdown positioned at top right corner */}
      {canToggleCompletion && (
        <div className="absolute top-4 right-4 z-20">
          <EventActionsDropdown
            jamNight={event}
            canManageEvent={canToggleCompletion}
            variant="icon"
            onActionStart={() => setPreventNavigation(true)}
            onActionEnd={() => setPreventNavigation(false)}
          />
        </div>
      )}

      <CardHeader className="relative z-10 pr-12">
        <CardTitle className="text-slate-900/90 font-semibold">
          <div className="mb-2 pr-2">
            <span className="break-words">{event.name}</span>
          </div>
        </CardTitle>
        {event.venue && (
          <CardDescription className="space-y-2">
            <div className="flex items-center text-sm text-slate-700/80">
              <MapPin className="h-4 w-4 mr-2 drop-shadow-sm" />
              {event.venue}
            </div>
          </CardDescription>
        )}
      </CardHeader>
      {event.description ? (
        <CardContent className="relative z-10 -mt-4 flex-grow">
          <p className="text-sm text-slate-700/80 line-clamp-2 drop-shadow-sm">
            {event.description}
          </p>
        </CardContent>
      ) : (
        <div className="flex-grow" />
      )}
      <CardContent className="pt-0 relative -mt-3 z-10 ">
        {(event.completed || event.signupLimit || event.signupUrl || !event.signupUrl) && (
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {event.completed && (
              <>
                <Lock className="h-4 w-4 text-slate-600 drop-shadow-sm flex-shrink-0" />
                <StyledBadge variant="completed">
                  Completed
                </StyledBadge>
              </>
            )}
            {event.signupLimit && event.signupUrl && event.signupEnabled && (
              <StyledBadge variant="signupLimit" title="Signup limit for public registrations only">
                <Users className="h-3 w-3 mr-1" />
                Signup Limit: {event.signupLimit}
              </StyledBadge>
            )}
            {event.signupUrl && event.signupEnabled ? (
              <StyledBadge variant="publicSignups" title="Public signups are open">
                <Link className="h-3 w-3 mr-1" />
                Public Signups: Enabled
              </StyledBadge>
            ) : (
              <StyledBadge variant="manualRegistration" title="Manual registration only">
                <Lock className="h-3 w-3 mr-1" />
                Manual Registration
              </StyledBadge>
            )}
          </div>
        )}
        <div className="flex items-center gap-2 mb-3">
          <StyledBadge variant="performers">
            <Users className="h-3 w-3 mr-1" />
            {event.performerCount} {event.type === 'generic'
              ? (event.performerCount === 1 ? 'Signup' : 'Signups')
              : (event.performerCount === 1 ? 'Performer' : 'Performers')
            }
          </StyledBadge>
        </div>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <StyledBadge variant="date">
              <Calendar className="h-3 w-3 mr-1 drop-shadow-sm" />
              {format(new Date(event.date), "d MMM yy p")}
            </StyledBadge>
          </div>
          <StyledBadge variant={event.type === "jam" ? "jam" : event.type === "openmic" ? "openmic" : "event"}>
            {event.type === "jam" ? "Jam" : event.type === "openmic" ? "Open Mic" : "Event"}
          </StyledBadge>
        </div>
      </CardContent>
    </Card>
  );
}
